import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import api from "../../lib/api";
import {
  Sparkles, AlertTriangle, CloudRain, CloudSun, Cloud, CloudLightning, Wind, Snowflake,
  Droplets, Thermometer, TrendingUp, TrendingDown, Bug, Sprout, Wallet, Landmark,
  Package, PackageMinus, Fuel, Wrench, Wheat, Info, CheckCircle, ChevronRight, Leaf,
} from "lucide-react";

const pick = (lang, hi, en) => (lang === "English" ? en : hi);

const ICONS = {
  "alert-triangle": AlertTriangle, "cloud-rain": CloudRain, "cloud-sun": CloudSun, cloud: Cloud,
  "cloud-lightning": CloudLightning, wind: Wind, snowflake: Snowflake, droplets: Droplets,
  thermometer: Thermometer, "trending-up": TrendingUp, "trending-down": TrendingDown, bug: Bug,
  sprout: Sprout, wallet: Wallet, landmark: Landmark, package: Package, "package-minus": PackageMinus,
  fuel: Fuel, wrench: Wrench, wheat: Wheat, info: Info, "check-circle": CheckCircle, leaf: Leaf,
};

const SEV = {
  red: { c: "#e11d48", chip: "rgba(225,29,72,0.12)", bar: "#e11d48" },
  amber: { c: "#d97706", chip: "rgba(217,119,6,0.12)", bar: "#d97706" },
  green: { c: "#2f9e3a", chip: "rgba(47,158,58,0.12)", bar: "#2f9e3a" },
  info: { c: "#2563eb", chip: "rgba(37,99,235,0.10)", bar: "#2563eb" },
  blue: { c: "#2563eb", chip: "rgba(37,99,235,0.10)", bar: "#2563eb" },
};

function InsightCard({ card, language, onGo }) {
  const Icon = ICONS[card.icon] || Sparkles;
  const s = SEV[card.severity] || SEV.info;
  return (
    <motion.button
      onClick={() => onGo(card)}
      whileTap={{ scale: 0.97 }}
      data-testid={`ai-insight-${card.id}`}
      className="relative snap-start shrink-0 w-[240px] sm:w-auto text-left rounded-2xl bg-white/85 border border-black/5 p-4 shadow-sm hover:shadow-md transition-shadow overflow-hidden"
    >
      <span className="absolute left-0 top-0 bottom-0 w-1.5" style={{ background: s.bar }} />
      <div className="flex items-start gap-3 pl-1.5">
        <span className="h-10 w-10 rounded-xl flex items-center justify-center shrink-0" style={{ background: s.chip }}>
          <Icon style={{ color: s.c, height: 20, width: 20 }} />
        </span>
        <div className="min-w-0 flex-1">
          <p className="text-[13.5px] font-bold text-[#1c2b1e] leading-snug">{card.title}</p>
          {card.reason && <p className="text-[12px] text-[#55634f] mt-1 leading-relaxed">{card.reason}</p>}
          <span className="mt-2 inline-flex items-center gap-1 text-[12px] font-bold" style={{ color: s.c }}>
            {card.action_label || pick(language, "देखें", "View")} <ChevronRight className="h-3.5 w-3.5" />
          </span>
        </div>
      </div>
    </motion.button>
  );
}

export function AiAdvisorRail({ language, item }) {
  const navigate = useNavigate();
  const [cards, setCards] = useState(null);

  useEffect(() => {
    let live = true;
    api.get(`/ai/insights?context=dashboard&language=${encodeURIComponent(language)}`)
      .then((r) => { if (live) setCards(r.data?.cards || []); })
      .catch(() => { if (live) setCards([]); });
    return () => { live = false; };
  }, [language]);

  const go = (card) => { if (card.target) navigate(card.target); };

  if (cards && cards.length === 0) return null;

  return (
    <motion.div variants={item} className="agro-glass rounded-[22px] p-5 mb-5" data-testid="ai-advisor-rail">
      <div className="flex items-center justify-between mb-1">
        <h2 className="font-display font-bold text-[18px] text-[#173f1e] flex items-center gap-2">
          <span className="h-8 w-8 rounded-xl bg-[#eaf7ec] flex items-center justify-center">
            <Sparkles className="h-4.5 w-4.5 text-[#2f9e3a]" style={{ height: 18, width: 18 }} />
          </span>
          {pick(language, "AI सुझाव", "AI Advisor")}
        </h2>
        <span className="text-[10px] font-semibold text-[#2f7d33] bg-[#eaf7ec] rounded-full px-2.5 py-0.5">
          {pick(language, "आपके खेत के लिए", "For your farm")}
        </span>
      </div>
      <p className="text-[12px] text-[#6c7a67] mb-3">
        {pick(language, "आपके खेत के आँकड़ों से बनी आसान सलाह — एक नज़र में क्या करना है 🌱", "Smart, simple advice from your farm data — what to do, at a glance 🌱")}
      </p>

      {!cards ? (
        <div className="flex gap-3 overflow-hidden" data-testid="ai-advisor-loading">
          {[0, 1, 2].map((i) => (
            <div key={i} className="shrink-0 w-[240px] sm:flex-1 h-[92px] rounded-2xl bg-black/[0.04] animate-pulse" />
          ))}
        </div>
      ) : (
        <div className="flex sm:grid sm:grid-cols-2 gap-3 overflow-x-auto sm:overflow-visible snap-x snap-mandatory -mx-1 px-1 pb-1 no-scrollbar">
          {cards.map((c) => <InsightCard key={c.id} card={c} language={language} onGo={go} />)}
        </div>
      )}
    </motion.div>
  );
}
